import { Button, Dropdown, Segment } from '@mairistem/composants';
import { Liste, ListeTable } from '@mairistem/liste';
import _ from 'lodash';
import React, { type ReactNode } from 'react';

import { CycleContext, FacturePrelevementContext, LoadingContext } from '@/contexts';
import type { FactureListePrelevementItemType } from '@/types/facture';
import { formatFacturesToPrelevementListeItem } from '@/utils/facture';
import { useRegieStore } from '@/zustand';
import PrelevementsListeTableHeader from './PrelevementsListeTableHeader';
import PrelevementsListeTableRow from './PrelevementsListeTableRow';

import './PrelevementsListe.less';

interface PrelevementsListeProps {
  selectedItems: FactureListePrelevementItemType[] | null;
  selectedCycle: number;
  loading?: boolean;
  onItemSelect: (selection: FactureListePrelevementItemType[]) => void;
  onCycleSelect?: (value: number) => void;
  onRejeterClick?: () => void;
  onValiderClick?: () => void;
  mode: 'valider' | 'rejeter';
}

const PrelevementsListe = (props: PrelevementsListeProps) => {
  const {
    selectedCycle,
    selectedItems,
    loading,
    onItemSelect,
    onCycleSelect,
    onRejeterClick,
    onValiderClick,
    mode,
  } = props;

  const encaissementPossible = useRegieStore((state) => state.regie?.encaissementPossible);

  const { total, factures, search, setSearch, setPage } =
    React.useContext(FacturePrelevementContext);

  const { cycles } = React.useContext(CycleContext);

  const {
    facturesLoadings: { factures: facturesLoading },
  } = React.useContext(LoadingContext);

  const formatedFactures = React.useMemo<FactureListePrelevementItemType[]>(
    () => formatFacturesToPrelevementListeItem(factures),
    [JSON.stringify(factures)],
  );

  const handleSearch = React.useCallback(
    async (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setPage, setSearch],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const handleItemSelect = React.useCallback(
    (
      selectedItem: FactureListePrelevementItemType,
      allSelected: FactureListePrelevementItemType[],
    ) => {
      // Si clic sur tout sélectionner / désélectionner
      if (_.isNil(selectedItem)) {
        // Si on veut déselectionner tout ce qu'il y a dans la liste
        if (_.isEmpty(allSelected)) {
          onItemSelect?.(
            _.filter(selectedItems, (item) => {
              // Si l'élément est actuellement affiché dans la liste malgré la recherche / cycle
              // On le retire
              if (_.some(formatedFactures, { identifiant: item.identifiant })) {
                return false;
              }

              // Sinon on le garde car il n'est pas concerné
              return true;
            }),
          );
        } else {
          // On veut sélectionner tout ce qu'il y a dans la liste
          onItemSelect?.(formatedFactures);
        }
      } else if (_.some(selectedItems, { identifiant: selectedItem.identifiant })) {
        // Clic sur un élément précis
        // Si l'élément est déjà sélectionné, on le retire
        onItemSelect?.(
          _.filter(selectedItems, (item) => item.identifiant !== selectedItem.identifiant),
        );
      } else {
        onItemSelect?.([...selectedItems, selectedItem]);
      }
    },
    [JSON.stringify(formatedFactures), onItemSelect, selectedItems],
  );

  const handleCycleSelect = React.useCallback(
    (e, data) => {
      onCycleSelect?.(data.value);
    },
    [onCycleSelect],
  );

  const renderRow = (item, __, rowProps) => (
    <PrelevementsListeTableRow item={item} rowProps={rowProps} />
  );

  const cyclesOptions = React.useMemo(
    () =>
      _.map(_.orderBy(cycles, 'titre'), (cycle) => ({
        key: cycle.identifiant,
        value: cycle.identifiant,
        text: cycle.titre,
      })),
    [cycles],
  );

  return (
    <Segment className="liste-prelevements" basic style={{ margin: 0, padding: 0, height: '100%' }}>
      <Liste
        title=""
        loading={loading || facturesLoading || _.isNil(factures)}
        onSearch={handleSearch}
        searchable={mode === 'rejeter'}
        search={{
          value: search,
          placeholder: 'Rechercher un titulaire de compte ou un cycle de facturation',
          clearable: true,
          autoFocus: true,
        }}
      >
        <Liste.Header>
          <Dropdown
            selection
            options={[
              {
                key: 'all',
                value: 0,
                text: 'Tous les cycles',
              },
              ...cyclesOptions,
            ]}
            defaultValue={0}
            value={selectedCycle}
            onChange={handleCycleSelect}
            style={{
              width: 250,
              visibility: mode === 'rejeter' ? 'hidden' : 'visible',
            }}
          />
        </Liste.Header>
        <Liste.Content>
          <div style={{ height: 450 }}>
            <ListeTable
              count={_.isEmpty(formatedFactures) ? 0 : total}
              items={formatedFactures}
              loading={loading || facturesLoading || _.isNil(factures)}
              perPage={30}
              onLoad={handleLoad}
              {...(mode === 'rejeter' && {
                selection: 'checkbox',
                onSelect: handleItemSelect,
                noResultsIcon: '',
                noResultsMessage: _.isEmpty(search)
                  ? "Il n'y a aucun cycle de facturation avec prélèvement en cours"
                  : "Il n'y a aucun cycle de facturation avec prélèvement en cours correspondant à votre recherche",
              })}
              {...(mode === 'valider' && {
                noResultsMessage: "Il n'y a aucun cycle de facturation avec prélèvement en cours",
                noResultsIcon: '',
              })}
              // Recréer le tableau dans selectedItems permet de contourner un bug
              // Où si le tableau est le même pendant la recherche puis
              // Après le RAZ du champs recherche, seulement le dernier élément
              // sélectionné reste affiché sélectionné
              selectedItems={mode === 'rejeter' ? [...selectedItems] : []}
            >
              <ListeTable.Header>
                <PrelevementsListeTableHeader />
              </ListeTable.Header>
              {renderRow as unknown as ReactNode}
            </ListeTable>
          </div>
        </Liste.Content>
      </Liste>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '1em',
        }}
      >
        {onRejeterClick && !_.isEmpty(selectedItems) && (
          <Button
            negative
            size="large"
            content={
              _.size(selectedItems) === 1
                ? 'Saisir le rejet pour la facture sélectionnée'
                : `Saisir le rejet pour les ${_.size(selectedItems)} factures sélectionnées`
            }
            onClick={onRejeterClick}
            style={{ marginRight: onValiderClick ? '1em' : undefined }}
          />
        )}
        {onValiderClick && (
          <Button
            positive
            size="large"
            disabled={
              encaissementPossible === false || loading || facturesLoading || _.isEmpty(factures)
            }
            loading={loading || facturesLoading || _.isNil(factures)}
            content={
              _.isEmpty(formatedFactures)
                ? 'Générer les reçus'
                : _.size(formatedFactures) === 1
                  ? 'Générer le reçu'
                  : `Générer les ${_.size(formatedFactures)} reçus`
            }
            onClick={onValiderClick}
          />
        )}
      </div>
    </Segment>
  );
};

export default PrelevementsListe;
